import graphql__de4e697e6b596f435b41 from "./__generated__/useSimilarAnswersFetchQuery.graphql.js";import { useState, useCallback, useEffect, useRef } from "react";
import { fetchQuery, graphql } from "react-relay";

import appRelayEnvironment from "../../config/AppRelayEnvironment";
import { countWords } from "../Utils/countWords";

// Minimum word count before any similarity query is fired. Exported so sibling hooks
// can apply the same threshold without duplicating the constant.
export const MIN_WORD_COUNT = 10;

// Handles the async fetch layer: fires the similarity query, stores results, tracks
// loading state, and guards against race conditions via a monotonic version counter.
export const useSimilarAnswersFetch = (
  formFieldId,
  answerId,
  displaySimilarContributionsSuggestions
) => {
  const [similarAnswers, setSimilarAnswers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestVersionRef = useRef(0);

  const fetchSimilarAnswers = useCallback(
    async (queryText, ignoredAnswerIds = []) => {
      if (!displaySimilarContributionsSuggestions || !formFieldId) return;

      if (countWords(queryText) < MIN_WORD_COUNT) {
        setSimilarAnswers([]);
        return;
      }

      // Relay's fetchQuery has no abort API; a monotonic version counter lets us
      // silently discard responses from overlapping or superseded fetches.
      const version = ++requestVersionRef.current;
      setIsLoading(true);

      try {
        const data = await fetchQuery(
          appRelayEnvironment,
          useSimilarAnswersFetchQuery,
          {
            queryText,
            formFieldId,
            answerId,
            ignoredAnswerIds
          },
          {
            fetchPolicy: "network-only"
          }
        ).toPromise();

        // If the requestVersionRef.current has changed since this fetch was initiated, it
        // means a newer fetch has been started and this response is stale.
        if (version !== requestVersionRef.current) return;

        if (data?.similarAnswers) {
          setSimilarAnswers(data.similarAnswers);
        }
      } catch {
        if (version !== requestVersionRef.current) return;

        setSimilarAnswers([]);
      } finally {
        // Guard the loading reset with the same version check: a superseded fetch
        // must not clear the loading state that belongs to the current fetch.
        if (version === requestVersionRef.current) setIsLoading(false);
      }
    },
    [displaySimilarContributionsSuggestions, formFieldId, answerId]
  );

  useEffect(() => {
    return () => {
      // -1 is never a valid version, so any in-flight fetch that resolves after
      // unmount finds a mismatch and returns without touching state.
      requestVersionRef.current = -1;
    };
  }, []);

  return { fetchSimilarAnswers, similarAnswers, isLoading };
};

// Only fetch the contribution Relay ID from each similar answer. Full contribution data
// (title, content, author, votes) is lazy-loaded by SuggestionCardContent when the user
// opens the review funnel.
const useSimilarAnswersFetchQuery = graphql__de4e697e6b596f435b41;
